<template>
  <div class="approval-diff-panel">
    <p v-if="approval.submitable" class="text-xs text-gray-400 dark:text-gray-500 mb-3">
      {{ approval.submitable.label }}
      <span v-if="approval.submitted_at"> · {{ approval.submitted_at }}</span>
    </p>

    <div
      v-for="subject in approval.payload"
      :key="subject.subject_type + subject.subject_id"
      class="mb-4 last:mb-0"
    >
      <div v-if="(approval.payload?.length ?? 0) > 1" class="flex items-center gap-2 mb-2">
        <p class="text-xs font-semibold text-gray-500 dark:text-gray-400">
          {{ subject.subject_label }}
        </p>
      </div>

      <!-- Overview: how many fields actually changed -->
      <p v-if="!subject.is_create" class="diff-summary" :class="changedFieldsCount(subject) > 0 ? 'diff-summary-changed' : 'diff-summary-none'">
        <span v-if="changedFieldsCount(subject) > 0" class="diff-summary-dot" />
        {{ changedFieldsCount(subject) > 0 ? `${changedFieldsCount(subject)} فیلد تغییر کرده` : 'هیچ فیلدی تغییر نکرده' }}
      </p>

      <!-- Desktop table -->
      <div class="hidden sm:block overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
        <table class="diff-table w-full text-sm border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-700/60">
              <th class="diff-th">فیلد</th>
              <th v-if="!subject.is_create" class="diff-th diff-th-old">مقدار قبلی</th>
              <th class="diff-th diff-th-new">{{ subject.is_create ? 'مقدار' : 'مقدار جدید' }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="field in subject.fields"
              :key="field.key"
              :class="isFieldChanged(field) ? 'diff-row-changed' : 'diff-row-unchanged'"
            >
              <td class="diff-td diff-td-label">
                <span v-if="isFieldChanged(field)" class="diff-changed-dot" />
                {{ field.label }}
              </td>
              <td v-if="!subject.is_create" class="diff-td diff-td-old">
                <DiffValue :field="field" side="original" @preview="lightbox = $event" />
              </td>
              <td class="diff-td diff-td-new">
                <DiffValue :field="field" side="new" @preview="lightbox = $event" />
                <span v-if="isFieldChanged(field)" class="badge-changed">تغییر یافته</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards -->
      <div class="sm:hidden space-y-2">
        <div
          v-for="field in subject.fields"
          :key="'m-' + field.key"
          class="rounded-lg border overflow-hidden"
          :class="isFieldChanged(field) ? 'border-amber-300 dark:border-amber-700 ring-2 ring-amber-200 dark:ring-amber-900/60' : 'border-gray-200 dark:border-gray-600 opacity-80'"
        >
          <div
            class="px-3 py-2 text-xs font-semibold flex items-center gap-1.5"
            :class="isFieldChanged(field) ? 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300' : 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'"
          >
            <span v-if="isFieldChanged(field)" class="diff-changed-dot" />
            {{ field.label }}
            <span v-if="isFieldChanged(field)" class="badge-changed mr-auto">تغییر یافته</span>
          </div>
          <div v-if="!subject.is_create" class="px-3 py-2 border-b border-gray-100 dark:border-gray-700 bg-red-50/50 dark:bg-red-950/20">
            <p class="text-[10px] text-gray-400 mb-0.5">مقدار قبلی</p>
            <DiffValue :field="field" side="original" @preview="lightbox = $event" />
          </div>
          <div class="px-3 py-2" :class="isFieldChanged(field) ? 'diff-new-changed' : ''">
            <p class="text-[10px] text-gray-400 mb-0.5">{{ subject.is_create ? 'مقدار' : 'مقدار جدید' }}</p>
            <div class="flex items-center gap-2 flex-wrap">
              <DiffValue :field="field" side="new" @preview="lightbox = $event" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Actions -->
    <p v-if="!canApprove && blockedReason" class="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 mt-3">
      {{ blockedReason }}
    </p>

    <div v-if="canApprove && rejectingId === approval.id" class="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3 sm:p-4 mt-3">
      <p class="text-sm font-semibold text-red-700 dark:text-red-400 mb-2">دلیل رد</p>
      <textarea
        :value="rejectReason"
        rows="3"
        placeholder="دلیل رد..."
        class="w-full rounded-md border border-red-200 dark:border-red-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm resize-none"
        @input="$emit('update:reject-reason', $event.target.value)"
      />
      <div class="flex flex-col sm:flex-row gap-2 mt-3">
        <button
          class="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
          :disabled="actionLoading || !rejectReason.trim()"
          @click="$emit('reject', approval)"
        >
          تأیید رد
        </button>
        <button class="text-sm text-gray-500 py-2" @click="$emit('cancel-reject')">انصراف</button>
      </div>
    </div>

    <!-- Actions -->
    <div v-else-if="canApprove" class="flex flex-col sm:flex-row gap-2 mt-3">
      <button
        class="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
        :disabled="actionLoading"
        @click="$emit('approve', approval)"
      >
        تأیید
      </button>
      <button
        class="rounded-lg border border-red-200 dark:border-red-800 px-4 py-2 text-sm font-semibold text-red-600 dark:text-red-400 disabled:opacity-60"
        :disabled="actionLoading"
        @click="$emit('start-reject', approval)"
      >
        رد
      </button>
    </div>

    <div v-if="lightbox" class="lightbox-overlay" @click="lightbox = null">
      <img :src="lightbox" class="lightbox-img" />
    </div>
  </div>
</template>

<script>
import { resolveVideoPlayback } from '@modules/system/templates/js/utils/videoEmbed.js'

const ApprovalVideoPreview = {
  name: 'ApprovalVideoPreview',
  props: { url: { type: String, default: null } },
  computed: {
    playback() {
      return resolveVideoPlayback(this.url)
    },
  },
  template: `
    <div v-if="playback" class="video-preview">
      <iframe
        v-if="playback.mode === 'iframe'"
        :src="playback.src"
        class="video-frame"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowfullscreen
        referrerpolicy="no-referrer-when-downgrade"
        title="پیش‌نمایش ویدیو"
      />
      <video
        v-else
        :src="playback.src"
        controls
        playsinline
        class="video-frame"
      />
      <a
        v-if="url"
        :href="url"
        target="_blank"
        rel="noopener noreferrer"
        class="video-link"
      >{{ url }}</a>
    </div>
    <span v-else class="empty">—</span>
  `,
}

const DiffValue = {
  props: { field: Object, side: String },
  emits: ['preview'],
  components: { ApprovalVideoPreview },
  render() {
    const data = this.field[this.side]
    const h = window.Vue.h

    if (this.field.type === 'image' && data?.simulateValue) {
      return h('img', {
        src: data.simulateValue,
        class: 'thumb cursor-pointer max-h-10',
        onClick: () => this.$emit('preview', data.simulateValue),
      })
    }

    if (this.field.type === 'video') {
      return h(ApprovalVideoPreview, { url: data?.simulateValue ?? null })
    }

    return h('span', { class: 'text-gray-700 dark:text-gray-200 break-words' }, data?.simulateValue ?? '—')
  },
}

export default {
  name: 'ApprovalDiffPanel',
  components: { DiffValue },

  props: {
    approval: { type: Object, required: true },
    rejectingId: { type: [Number, null], default: null },
    rejectReason: { type: String, default: '' },
    actionLoading: { type: Boolean, default: false },
    canApprove: { type: Boolean, default: true },
    blockedReason: { type: String, default: null },
  },

  emits: ['approve', 'reject', 'start-reject', 'cancel-reject', 'update:reject-reason'],

  data: () => ({ lightbox: null }),

  methods: {
    isFieldChanged(field) {
      // Normalize null/undefined so a real null → value transition is still
      // detected as a change (previously any null original was treated as "unchanged").
      const original = field.original?.value ?? null
      const updated = field.new?.value ?? null
      return original !== updated
    },

    changedFieldsCount(subject) {
      return (subject.fields ?? []).filter((field) => this.isFieldChanged(field)).length
    },
  },
}
</script>

<style scoped>
.diff-th {
  padding: 10px 14px;
  text-align: right;
  font-size: 12px;
  font-weight: 600;
  color: rgba(var(--colors-gray-500), 1);
  border-bottom: 1px solid rgba(var(--colors-gray-200), 1);
}

.diff-th:not(:last-child) {
  border-left: 1px solid rgba(var(--colors-gray-200), 1);
}

.diff-td {
  padding: 10px 14px;
  vertical-align: middle;
  border-bottom: 1px solid rgba(var(--colors-gray-100), 1);
}

.diff-td:not(:last-child) {
  border-left: 1px solid rgba(var(--colors-gray-100), 1);
}

.diff-td-label {
  font-weight: 500;
  color: rgba(var(--colors-primary-600), 1);
  background: rgba(var(--colors-primary-50), 1);
  white-space: nowrap;
  width: 1%;
}

.diff-td-old {
  color: rgba(var(--colors-gray-400), 1);
  background: rgba(var(--colors-red-50), 0.35);
}

.diff-td-new {
  color: rgba(var(--colors-gray-700), 1);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* Unchanged rows recede slightly so changed rows pop out at a glance */
.diff-row-unchanged .diff-td-old,
.diff-row-unchanged .diff-td-new {
  opacity: 0.75;
}

.diff-row-changed {
  background: rgba(234, 179, 8, 0.18);
}

/* Accent bar on a td (not tr) so it paints reliably with border-collapse */
.diff-row-changed .diff-td-label {
  box-shadow: inset 3px 0 0 rgb(217, 119, 6);
}

.diff-row-changed .diff-td-new {
  font-weight: 700;
  color: rgb(146, 64, 14);
}

.diff-changed-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background: rgb(217, 119, 6);
  margin-left: 6px;
  flex-shrink: 0;
}

.badge-changed {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 9999px;
  font-weight: 700;
  background: rgb(217, 119, 6);
  color: #fff;
  white-space: nowrap;
}

.diff-summary {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
}

.diff-summary-changed {
  color: rgb(146, 64, 14);
}

.diff-summary-none {
  color: rgba(var(--colors-gray-400), 1);
  font-weight: 500;
}

.diff-summary-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 9999px;
  background: rgb(217, 119, 6);
}

.diff-new-changed {
  background: rgba(234, 179, 8, 0.18);
}

.thumb {
  border-radius: 4px;
  border: 1px solid rgba(var(--colors-gray-200), 1);
}

.lightbox-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
}

.lightbox-img {
  max-width: 92vw;
  max-height: 85vh;
  border-radius: 8px;
  object-fit: contain;
}

html.dark .diff-th,
.dark .diff-th {
  border-color: rgba(var(--colors-gray-600), 1);
  color: rgba(var(--colors-gray-300), 1);
  background: rgba(var(--colors-gray-800), 1);
}

html.dark .diff-td,
.dark .diff-td {
  border-color: rgba(var(--colors-gray-700), 1);
}

html.dark .diff-td-label,
.dark .diff-td-label {
  background: rgba(var(--colors-primary-900), 0.4);
  color: rgba(var(--colors-primary-300), 1);
}

html.dark .diff-td-old,
.dark .diff-td-old {
  background: rgba(var(--colors-red-950), 0.3);
  color: rgba(var(--colors-gray-200), 1);
}

html.dark .diff-row-changed,
.dark .diff-row-changed {
  background: rgba(234, 179, 8, 0.14);
}

html.dark .diff-row-changed .diff-td-label,
.dark .diff-row-changed .diff-td-label {
  box-shadow: inset 3px 0 0 rgb(217, 119, 6);
}

html.dark .diff-row-changed .diff-td-new,
.dark .diff-row-changed .diff-td-new {
  color: rgb(252, 211, 77);
}

html.dark .diff-summary-changed,
.dark .diff-summary-changed {
  color: rgb(252, 211, 77);
}
</style>

<style>
/* Unscoped: inline ApprovalVideoPreview template is not a SFC child */
.approval-diff-panel .video-preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.approval-diff-panel .video-frame {
  width: 100%;
  max-width: 360px;
  aspect-ratio: 16 / 9;
  border: 0;
  border-radius: 8px;
  background: #000;
}

.approval-diff-panel .video-link {
  font-size: 11px;
  color: rgba(var(--colors-primary-600), 1);
  word-break: break-all;
  text-decoration: underline;
}

.approval-diff-panel .empty {
  color: rgba(var(--colors-gray-400), 1);
}
</style>
